<template>
  <div class="p-2 advanced-table-wrapper">
    <div v-if="isLoading" class="loading">
      <img src="@/assets/images/loading.gif" alt="Loading" />
    </div>
    <template v-else>
      <div class="table-wrapper">
        <table class="advanced-table">
          <thead>
            <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :style="{ width: `${header.getSize()}px` }"
                :colSpan="header.colSpan"
              >
                <FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in table.getRowModel().rows" :key="row.id" :class="table.options.meta?.getRowClasses(row)">
              <td v-for="cell in row.getVisibleCells()" :key="cell.id">

                <template v-if="cell.column.id === 'productCode'">
                  <div v-if="cell.row.original.sellerStockCode" class="error-column-item">
                    <img
                      width="20px"
                      height="20px"
                      src="@/assets/images/icons/alert-circle.svg"
                      alt="Error Icon"
                    />
                    <span>{{ cell.row.original.sellerStockCode }}</span>
                  </div>
                  <span v-else>-</span>
                </template>
            
                <template v-if="cell.column.id === 'message'">
                  <span v-if="cell.column.id === 'message'">
                      {{ cell.row.original.reasons[0] }}
                  </span>
                </template>
                <template>
                  <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination flex items-center gap-2">
        <Pagination
          :pageCount="table.getPageCount()"
          :clickHandler="clickHandler"
          :value="table.getState().pagination.pageIndex"
          :pageRange="5"
          firstLastButton="true"
          containerClass="button-group"
          prevLinkClass="border left rounded p-1"
          pageLinkClass="border rounded p-1"
          nextLinkClass="border right rounded p-1"
          :marginPages="0"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref} from 'vue';
import Pagination from '@/components/Table/Pagination';
import useService from '../services/getRejectReasonService';
import { getRejectReasonColumns} from '../services/getBulkHistoryColumns';
import { FlexRender, getCoreRowModel, useVueTable } from '@tanstack/vue-table';

const INITIAL_PAGE_INDEX = 0;
const INITIAL_PAGE_SIZE = 10;

const props = defineProps({
  taskId: {
    type: String || Number,
    required: true
  },
});
const highlightedRows = ref([]);
const pagination = ref({
  pageIndex: INITIAL_PAGE_INDEX,
  pageSize: INITIAL_PAGE_SIZE,
  type: props.selectedTabIndex,
  reload: false
});

const tableColumns = ref(getRejectReasonColumns());
const { data, pageCount, isLoading } = useService(pagination,props.taskId);

const table = ref(
  useVueTable({
    get data() {
      return data?.value ?? [];
    },
    get pageCount() {
      return pageCount.value;
    },
    get columns() {
      return tableColumns.value;
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    getRowId: row => row.id,
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const updated = updater(pagination.value);
        setPagination(updated);
      } else {
        setPagination(updater);
      }
    },
    get rowCount() {
      return data?.value?.length ?? 0;
    },
    state: {
      get pagination() {
        return pagination.value;
      }
    },
    meta: {
      getRowClasses: (row) => highlightedRows.value.includes(row.original.taskId) ? 'success' : '',
    }
  })
);

const setPagination = ({ pageIndex, pageSize }) => {
  pagination.value = {
    ...pagination.value,
    pageIndex,
    pageSize,
  };
};

const clickHandler = (pageNum) => {
  table.value.setPageIndex(pageNum);
};

</script>
<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';
.advanced-table-wrapper {
  display: flex;
  gap: 16px;
  flex-direction: column;

  .loading {
    min-height: 647px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .table-wrapper {
    width: 100%;
    position: relative;
    border: 1px solid #d1d1d1;
    border-radius: 8px;
    border-bottom: 0px;
    border-spacing: 0;
    border-collapse: separate;
    overflow-y: auto;
    scroll-behavior: smooth;
    max-height: 308px;
  }

  .advanced-table {
    width: 100%;
    border-radius: 8px;
    border-collapse: separate;

    thead {
      background: $colorD10;
      tr {
        th {
          padding: 16px 12px;
          text-align: left;
          font-weight: 600;
          color: $colorN80;
					font-size: 13px;
          line-height: 18px;
        }
				th:not(:last-child) {
					border-right: 1px solid #D3D6DF;
				}
      }
    }

    tbody {
      tr {
        td {
          font-weight: 600;
          padding: 11px 12px;
          text-align: left;
          border-bottom: 2px solid $colorD10;
					font-size: 13px;
          letter-spacing: -0.05px;
          line-height: 18px;
        }
				td:not(:last-child) {
					border-right: 1px solid #D3D6DF;
				}

        &.success {
          background-color: $colorS05;
        }
      }
    }
  }
	.error-column-item {
		display: flex;
		font-size: 13px;
    align-items: flex-end;
    gap: 8px;
    line-height: 18px;
		span {
			color: $colorN80;
		}
	}

  .pagination {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
